<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  // { phone_number, status: 'pending' | 'rejected', reason } - see Whatsapp::EvolutionNumberMismatch.
  mismatch: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['accept', 'decline', 'close']);

const { t } = useI18n();
const { isAdmin } = useAdmin();
const dialogRef = ref(null);

const numbers = computed(() => ({
  scanned: `+${props.mismatch?.phone_number}`,
  current: props.inbox.phone_number,
}));
const isPending = computed(() => props.mismatch?.status === 'pending');

const timeoutNote = computed(() =>
  t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.TIMEOUT_NOTE', {
    ...numbers.value,
    time: new Date(props.mismatch.expires_at).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    }),
  })
);

const description = computed(() => {
  if (!props.mismatch) return '';
  if (isPending.value && isAdmin.value) {
    return `${t(
      'INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.PENDING_ADMIN',
      numbers.value
    )} ${timeoutNote.value}`;
  }
  if (isPending.value) {
    return `${t(
      'INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.PENDING_AGENT',
      numbers.value
    )} ${timeoutNote.value}`;
  }
  if (props.mismatch.reason === 'in_use') {
    return t(
      'INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.REJECTED_IN_USE',
      numbers.value
    );
  }
  return t(
    'INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.REJECTED',
    numbers.value
  );
});

// Already set when this mounts after a reload with a question waiting.
onMounted(() => {
  if (props.mismatch) dialogRef.value?.open();
});

watch(
  () => props.mismatch,
  (value, previous) => {
    if (value && !previous) dialogRef.value?.open();
    if (!value && previous) dialogRef.value?.close();
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    width="md"
    :title="t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.TITLE')"
    :description="description"
    @close="emit('close')"
  >
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <template v-if="isPending && isAdmin">
          <Button
            variant="faded"
            color="ruby"
            class="w-full"
            type="button"
            :label="
              t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.DECLINE')
            "
            :disabled="isLoading"
            @click="emit('decline')"
          />
          <Button
            color="blue"
            class="w-full"
            type="button"
            :label="
              t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.ACCEPT')
            "
            :is-loading="isLoading"
            :disabled="isLoading"
            @click="emit('accept')"
          />
        </template>
        <template v-else-if="isPending">
          <Button
            variant="faded"
            color="slate"
            class="w-full"
            type="button"
            :label="
              t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.CLOSE')
            "
            @click="dialogRef.close()"
          />
          <Button
            color="ruby"
            class="w-full"
            type="button"
            :label="
              t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.DECLINE')
            "
            :is-loading="isLoading"
            :disabled="isLoading"
            @click="emit('decline')"
          />
        </template>
        <Button
          v-else
          color="blue"
          class="w-full"
          type="button"
          :label="t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.GOT_IT')"
          @click="dialogRef.close()"
        />
      </div>
    </template>
  </Dialog>
</template>
